/**
 * Wording, sorting and small maths for แท็บ แอด (โฆษณา Meta) — pure, unit-tested in test/ads.spec.ts.
 * Money/count/percent formatting comes from lib/dash (money · count · pct); this file only adds what ads need.
 */
import type {
  AdFlag,
  AdMetrics,
  AdNode,
  AdsFreshness,
  AdsPeriod,
  AdsQuery,
  AdsReconcile,
  AdsWebSource,
  CreativeFormat,
  UnknownBucket,
} from '../api/ads/types'
import type { DashPeriod } from '../api/dash/types'
import type { IconName } from '../components/ui/icons'
import { formatAgo, shortDate } from './inbox'
import { money } from './dash'

/** The ads report follows the dashboard's period selector (range · offset · the two custom days). */
export function adsQueryOf(p: Pick<DashPeriod, 'range' | 'offset' | 'first_day' | 'last_day'>): AdsQuery {
  const custom = p.range === 'custom'
  return { range: p.range, offset: custom ? 0 : p.offset, from: custom ? (p.first_day ?? null) : null, to: custom ? (p.last_day ?? null) : null }
}

/** A stable key for "did the period change" watchers. */
export const queryKey = (q: AdsQuery): string => [q.range, q.offset, q.from ?? '', q.to ?? ''].join('|')

// ---------------------------------------------------------------- numbers

const nf2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const nf1 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

/** ROAS (revenue ÷ spend): 3.20 · — */
export const roasText = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : nf2.format(n))
/** Baht with satang, for small unit costs (CPC, CPM): ฿4.12 */
export const baht2 = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : `฿${nf2.format(n)}`)
/** CTR / hook-style small rates with two decimals: 1.34% */
export const ctrText = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : `${nf2.format(n)}%`)

/** A calmer daily trend: the mean of each day and the two before it (days without a value stay empty). */
export function rolling3(vs: (number | null)[]): (number | null)[] {
  return vs.map((v, i) => {
    if (v === null) return null
    const w = [vs[i - 2], vs[i - 1], v].filter((x): x is number => x !== null && x !== undefined)
    return w.reduce((a, b) => a + b, 0) / w.length
  })
}

/** Frequency (times a person saw the ad): 1.4 */
export const freqText = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : nf1.format(n))

/** Axis ticks: ฿0 · ฿500 · ฿1.5k · ฿12k */
export function tickBaht(v: number): string {
  if (v < 1000) return `฿${Math.round(v)}`
  const k = v / 1000
  return `฿${Number.isInteger(k) ? k : k.toFixed(1)}k`
}

/** A round upper bound for a chart axis (1 · 2 · 2.5 · 5 × 10ⁿ) at or above `v`. */
export function niceMax(v: number): number {
  if (!(v > 0)) return 1
  const p = 10 ** Math.floor(Math.log10(v))
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p
  return 10 * p
}

/** "แชท 12 · เว็บ 3" (the split behind the orders / revenue columns) */
export function splitTip(chat: number, web: number, asMoney = false): string {
  const f = (n: number) => (asMoney ? money(n) : String(n))
  return `แชท ${f(chat)} · เว็บ ${f(web)}`
}

// ---------------------------------------------------------------- labels

export const FLAG: Record<AdFlag, { text: string; tone: 'crit' | 'warn' | 'mute'; title: string }> = {
  no_sales: { text: 'ขายไม่ได้', tone: 'crit', title: 'ใช้เงินถึงเกณฑ์แจ้งเตือนแล้ว แต่ยังไม่มีแชทและออเดอร์' },
  cpo_high: { text: 'ต้นทุนสูง', tone: 'warn', title: 'ต้นทุนต่อออเดอร์เกินเกณฑ์ที่ตั้งไว้' },
  fatigue: { text: 'เริ่มล้า', tone: 'warn', title: 'คนเห็นซ้ำบ่อยขึ้นแต่คลิกน้อยลง เทียบกับ 3 วันแรกของแอด' },
  partial: { text: 'ยังนับไม่ครบ', tone: 'mute', title: 'ยอดขายยังเพิ่มได้ ลูกค้าที่มาจากแอดช่วงนี้ยังสั่งได้อีกภายในช่วงนับยอด' },
  tz: { text: 'เวลาไม่ตรง', tone: 'mute', title: 'บัญชีโฆษณานี้ไม่ได้ตั้งเวลาไทย วันของ Meta จึงไม่ตรงกับวันของร้าน' },
}
const FLAG_ORDER: AdFlag[] = ['no_sales', 'cpo_high', 'fatigue', 'partial', 'tz']

/**
 * Chips to draw for a row. `partial` is left out when every day of the period is still open (the summary line says it
 * once for the whole table instead of on every row).
 */
export function flagsOf(flags: AdFlag[], allOpen: boolean): AdFlag[] {
  return FLAG_ORDER.filter((f) => flags.includes(f) && !(f === 'partial' && allOpen))
}

export const FORMAT: Record<CreativeFormat, { label: string; icon: IconName }> = {
  video: { label: 'วิดีโอ', icon: 'play' },
  image: { label: 'รูป', icon: 'image' },
  carousel: { label: 'ภาพสไลด์', icon: 'layers' },
  dynamic: { label: 'ไดนามิก', icon: 'sparkle' },
  other: { label: 'อื่นๆ', icon: 'image' },
}

const CTA: Record<string, string> = {
  MESSAGE_PAGE: 'ส่งข้อความ',
  SEND_MESSAGE: 'ส่งข้อความ',
  WHATSAPP_MESSAGE: 'ส่งข้อความ WhatsApp',
  SHOP_NOW: 'ซื้อเลย',
  ORDER_NOW: 'สั่งเลย',
  BUY_NOW: 'ซื้อเลย',
  LEARN_MORE: 'ดูเพิ่มเติม',
  SIGN_UP: 'ลงทะเบียน',
  GET_OFFER: 'รับข้อเสนอ',
  CONTACT_US: 'ติดต่อเรา',
  NO_BUTTON: 'ไม่มีปุ่ม',
}
/** Meta call-to-action type → the words on the button. Unknown types stay readable ("BOOK_TRAVEL" → "book travel"). */
export function ctaLabel(cta: string | null | undefined): string {
  if (!cta) return '—'
  return CTA[cta] ?? cta.toLowerCase().replace(/_/g, ' ')
}

export const REASON: Record<UnknownBucket['reason'], string> = {
  not_synced: 'รหัสแอดนี้อยู่ในบัญชีที่ปิดการซิงก์ไว้ หรือยังไม่ได้ดึงข้อมูล',
  no_match: 'ไม่พบชื่อแคมเปญหรือแอดนี้ในบัญชีที่ซิงก์',
  ambiguous: 'มีแอดหลายตัวใช้ชื่อเดียวกัน จึงบอกไม่ได้ว่ามาจากตัวไหน',
  bad_param: 'ลิงก์ในแอดยังเป็นรหัสแม่แบบ {{…}} ควรตั้ง URL parameters ของแอดใหม่',
  post_only: 'คอมเมนต์ใต้โพสต์ที่หลายแอดใช้ร่วมกัน',
  no_ad_id: 'ลูกค้ามาจากแอด แต่ Meta ไม่ได้ส่งรหัสแอดมา',
  deleted: 'ค่าโฆษณาของแอดที่ถูกลบไปแล้ว Meta ไม่ส่งรายละเอียดรายแอดมา',
}

/** One line under a bucket's name: why we could not tie it to an ad (+ the candidates when ambiguous). */
export function reasonText(b: Pick<UnknownBucket, 'reason' | 'candidates'>): string {
  const base = REASON[b.reason] ?? 'ไม่ทราบแอด'
  return b.reason === 'ambiguous' && b.candidates?.length ? `${base}: ${b.candidates.join(' · ')}` : base
}

/** A server-made row ("unknown:…"): revenue tied only to its parent ad set / campaign — muted, not clickable. */
export const isSynthetic = (n: Pick<AdNode, 'id'>): boolean => n.id.startsWith('unknown:')

export const LEVEL: Record<AdNode['level'], string> = { campaign: 'แคมเปญ', adset: 'ชุดโฆษณา', ad: 'แอด' }

/** Under a row's name: "แคมเปญ · ข้อความ · 2 ชุดโฆษณา" / "ชุดโฆษณา · งบ ฿900/วัน" / "แอด · วิดีโอ". */
export function nodeSub(n: AdNode): string {
  const parts: string[] = [LEVEL[n.level]]
  if (n.level === 'campaign') {
    if (n.objective) parts.push(n.objective)
    if (n.children?.length) parts.push(`${n.children.length} ชุดโฆษณา`)
  } else if (n.level === 'adset') {
    if (n.children?.length) parts.push(`${n.children.length} แอด`)
  } else if (n.creative) parts.push(FORMAT[n.creative.format]?.label ?? 'อื่นๆ')
  if (n.daily_budget) parts.push(`งบ ${money(n.daily_budget)}/วัน`)
  return parts.join(' · ')
}

// ---------------------------------------------------------------- tree: sort · expand · flatten

export type SortKey = 'spend' | 'chats' | 'cost_per_chat' | 'orders' | 'revenue' | 'roas' | 'cost_per_order' | 'chat_to_order'
export type SortDir = 'asc' | 'desc'

export const SORT_COLS: { key: SortKey; label: string; title: string }[] = [
  { key: 'spend', label: 'ใช้จ่าย', title: 'ค่าโฆษณาที่ Meta คิดในช่วงนี้' },
  { key: 'chats', label: 'แชท', title: 'แชทในกล่องข้อความของเราที่มาจากแอดนี้' },
  { key: 'cost_per_chat', label: 'ต้นทุน/แชท', title: 'ค่าโฆษณา ÷ แชท' },
  { key: 'orders', label: 'ออเดอร์', title: 'ออเดอร์จากแชทและจากเว็บ (ชี้ที่ตัวเลขเพื่อดูแยก)' },
  { key: 'revenue', label: 'ยอดขาย', title: 'ยอดขายจากแชทและจากเว็บ' },
  { key: 'roas', label: 'ROAS', title: 'ยอดขาย ÷ ค่าโฆษณา (ขายได้กี่บาทต่อค่าแอด 1 บาท)' },
  { key: 'cost_per_order', label: 'ต้นทุน/ออเดอร์', title: 'ค่าโฆษณา ÷ ออเดอร์' },
  { key: 'chat_to_order', label: 'แชท→ออเดอร์', title: 'แชทที่สั่งซื้อภายในช่วงนับยอด ÷ แชททั้งหมด' },
]

/** Lower is better for costs: the first click on a cost column sorts low → high. */
export const firstDir = (k: SortKey): SortDir => (k === 'cost_per_chat' || k === 'cost_per_order' ? 'asc' : 'desc')

/** Compare rows by a number (null = — goes last whichever way); ties → more spend first → name. */
export function compareRows<T extends { spend: number; name?: string | null }>(val: (t: T) => number | null, dir: SortDir): (a: T, b: T) => number {
  return (a, b) => {
    const x = val(a)
    const y = val(b)
    if (x !== null && y === null) return -1
    if (x === null && y !== null) return 1
    if (x !== null && y !== null && x !== y) return dir === 'asc' ? x - y : y - x
    return b.spend - a.spend || (a.name ?? '').localeCompare(b.name ?? '')
  }
}

type Sortable = Pick<AdMetrics, SortKey> & { name?: string | null }

export function compareBy<T extends Sortable>(key: SortKey, dir: SortDir): (a: T, b: T) => number {
  return compareRows<T>((t) => t[key], dir)
}

/** Sort every level of the tree (campaigns, the ad sets inside each, the ads inside each) — returns new arrays. */
export function sortTree(nodes: AdNode[], key: SortKey, dir: SortDir): AdNode[] {
  return [...nodes]
    .sort(compareBy<AdNode>(key, dir))
    .map((n) => (n.children?.length ? { ...n, children: sortTree(n.children, key, dir) } : n))
}

/** Open by default: the top `n` campaigns by spend (one level: their ad sets show). */
export function defaultOpen(tree: AdNode[], n = 2): string[] {
  return [...tree]
    .filter((c) => c.spend > 0 && c.children?.length)
    .sort((a, b) => b.spend - a.spend)
    .slice(0, n)
    .map((c) => c.id)
}

export interface TreeRow {
  node: AdNode
  depth: number
  open: boolean
  hasKids: boolean
}

/** The rows on screen: every campaign, and the children of the open ones. */
export function visibleRows(tree: AdNode[], open: ReadonlySet<string>): TreeRow[] {
  const out: TreeRow[] = []
  const walk = (ns: AdNode[], depth: number) => {
    for (const n of ns) {
      const hasKids = !!n.children?.length
      const isOpen = hasKids && open.has(n.id)
      out.push({ node: n, depth, open: isOpen, hasKids })
      if (isOpen) walk(n.children!, depth + 1)
    }
  }
  walk(tree, 0)
  return out
}

/** Every id that can open (campaigns + ad sets) — "ขยายทั้งหมด". */
export function allParents(tree: AdNode[]): string[] {
  const out: string[] = []
  const walk = (ns: AdNode[]) => {
    for (const n of ns) {
      if (n.children?.length) {
        out.push(n.id)
        walk(n.children)
      }
    }
  }
  walk(tree)
  return out
}

/** Leaf ads of a tree (for counts and lookups). */
export function leaves(tree: AdNode[]): AdNode[] {
  const out: AdNode[] = []
  const walk = (ns: AdNode[]) => {
    for (const n of ns) (n.children?.length ? walk(n.children) : n.level === 'ad' && out.push(n))
  }
  walk(tree)
  return out
}

export function findNode(tree: AdNode[], id: string): AdNode | null {
  for (const n of tree) {
    if (n.id === id) return n
    const hit = n.children ? findNode(n.children, id) : null
    if (hit) return hit
  }
  return null
}

// ---------------------------------------------------------------- summary lines

/**
 * The footnote under the table: known ads + unknown = total. `ok` false when the parts do not add up (more than
 * half a baht / any chat off) — then the text says so instead of pretending.
 */
export function reconcileNote(r: AdsReconcile): { ok: boolean; text: string } {
  const revOk = Math.abs(r.revenue_rows + r.revenue_unknown - r.revenue_total) < 0.5
  const chatOk = r.chats_rows + r.chats_unknown === r.chats_total
  if (!revOk || !chatOk) {
    const diff = r.revenue_total - (r.revenue_rows + r.revenue_unknown)
    return {
      ok: false,
      text: revOk
        ? `จำนวนแชทไม่ตรงกัน: แถวแอด ${r.chats_rows} + ไม่ทราบแอด ${r.chats_unknown} ≠ รวม ${r.chats_total} แชท`
        : `ยอดไม่ตรงกัน ต่างกัน ${money(Math.abs(diff))}: แถวแอด ${money(r.revenue_rows)} + ไม่ทราบแอด ${money(r.revenue_unknown)} ≠ รวม ${money(r.revenue_total)}`,
    }
  }
  if (!r.revenue_unknown && !r.chats_unknown) return { ok: true, text: `ยอดขาย ${money(r.revenue_total)} และ ${r.chats_total} แชท ผูกกับแอดได้ทั้งหมด` }
  return {
    ok: true,
    text: `ยอดขายจากแอดที่รู้จัก ${money(r.revenue_rows)} + ไม่ทราบแอด ${money(r.revenue_unknown)} = ${money(r.revenue_total)} · แชท ${r.chats_rows} + ${r.chats_unknown} = ${r.chats_total}`,
  }
}

/** How many days of the period can still gain sales (their counting window is open). */
export function openInfo(p: Pick<AdsPeriod, 'day_list' | 'open_days'>): { all: boolean; n: number } {
  const open = p.open_days.filter((d) => p.day_list.includes(d)).length
  return { all: open > 0 && open === p.day_list.length, n: open }
}

export function openText(p: Pick<AdsPeriod, 'day_list' | 'open_days'>, windowDays: number): string | null {
  const o = openInfo(p)
  if (!o.n) return null
  if (o.all) return `ทุกวันในช่วงนี้ยังนับยอดไม่ครบ ${windowDays} วัน`
  return `${o.n} วันล่าสุดยังนับยอดไม่ครบ`
}

/** "อัปเดตจาก Meta ล่าสุด 12 นาทีที่แล้ว" (+ backfill / pause / error states in `extra`). */
export function freshText(f: AdsFreshness, now: number): { text: string; extra: string[]; error: string | null } {
  const ago = f.insights_at ? formatAgo(f.insights_at, now) : null
  const text = ago ? `อัปเดตจาก Meta ล่าสุด ${/^\d/.test(ago) ? `${ago}ที่แล้ว` : ago}` : 'ยังไม่ได้ดึงข้อมูลจาก Meta'
  const extra: string[] = []
  if (f.running) extra.push('กำลังดึงข้อมูล')
  if (!f.backfill_done) extra.push(f.data_from ? `กำลังดึงข้อมูลย้อนหลัง (มีถึง ${shortDate(`${f.data_from}T12:00:00+07:00`)})` : 'กำลังดึงข้อมูลย้อนหลัง')
  if (f.paused_until && Date.parse(f.paused_until) > now) extra.push(`Meta ให้พักการดึงถึง ${formatAgoClock(f.paused_until)}`)
  return { text, extra, error: f.last_error }
}

const hhmm = new Intl.DateTimeFormat('th-TH', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Bangkok' })
const formatAgoClock = (iso: string): string => `${hhmm.format(new Date(iso))} น.`

/** Where the web numbers came from, and whether to warn about it. */
export function webState(w: AdsWebSource): { text: string; tone: 'ok' | 'warn' | 'err'; chip: string | null } {
  if (!w.ok) return { text: 'ดึงยอดเว็บไม่ได้ — แสดงเฉพาะยอดแชท', tone: 'err', chip: null }
  const env = w.env === 'root' ? 'POS root' : w.env === 'beta' ? 'POS beta' : 'POS'
  if (w.env === 'beta') return { text: `ยอดเว็บจาก ${env}`, tone: 'warn', chip: 'beta เป็นสำเนา ตัวเลขจริงอยู่ที่ root' }
  return { text: `ยอดเว็บจาก ${env}`, tone: 'ok', chip: null }
}

/** "ยอดเว็บเริ่มนับ 8 ก.ย." when the period starts before web attribution existed. */
export function webSinceText(w: AdsWebSource, periodFrom: string): string | null {
  if (!w.ok || !w.data_since) return null
  return Date.parse(periodFrom) < Date.parse(w.data_since) ? `ยอดเว็บเริ่มนับ ${shortDate(w.data_since)}` : null
}

// ---------------------------------------------------------------- settings wording

export const maskToken = (last4: string | null | undefined): string => (last4 ? `••••${last4}` : '—')

export const BACKFILL: Record<'pending' | 'running' | 'done' | 'error', string> = {
  pending: 'ยังไม่ได้ดึงข้อมูลย้อนหลัง',
  running: 'กำลังดึงข้อมูลย้อนหลัง',
  done: 'ดึงข้อมูลย้อนหลังครบแล้ว',
  error: 'ดึงข้อมูลย้อนหลังไม่สำเร็จ',
}

/** "ครบ 90 วัน (ตั้งแต่ 29 มิ.ย.)" style sub-line for an account's backfill. */
export function backfillText(b: { state: 'pending' | 'running' | 'done' | 'error'; from: string | null; until: string | null }): string {
  const base = BACKFILL[b.state]
  if ((b.state === 'done' || b.state === 'running') && b.from) return `${base} ตั้งแต่ ${shortDate(`${b.from}T12:00:00+07:00`)}`
  return base
}
